import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import LogoutIcon from '@mui/icons-material/Logout';
import ComputerOutlinedIcon from '@mui/icons-material/ComputerOutlined';
import api from 'api/client';

const borderColor = 'rgba(245, 158, 11, 0.35)';

const cardSx = {
  border: '1px solid',
  borderColor,
  borderRadius: 3,
  boxShadow: 'none'
};

const actionButtonSx = {
  bgcolor: 'var(--fz-surface-1, #111827)',
  color: '#f59e0b',
  border: '1px solid #f59e0b',
  '&:hover': { bgcolor: 'var(--fz-surface-0, #0b1220)', borderColor: '#f59e0b' }
};

function parseError(error, fallback) {
  const payload = error?.response?.data;
  if (!payload) return error?.message || fallback;
  if (typeof payload === 'string') return payload;
  if (payload.detail) return payload.detail;
  return fallback;
}

const AdminSessionsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('q', search.trim());
      const suffix = params.toString() ? `?${params.toString()}` : '';
      const { data } = await api.get(`/api/admin/system/sessions/${suffix}`);
      setRows(Array.isArray(data?.results) ? data.results : []);
    } catch (err) {
      setError(parseError(err, 'Failed to load sessions.'));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  const revokeSession = async (sessionKey) => {
    setActionLoading(true);
    setError('');
    try {
      await api.post('/api/admin/system/sessions/', {
        action: 'revoke_session',
        session_key: sessionKey
      });
      await load();
    } catch (err) {
      setError(parseError(err, 'Failed to revoke session.'));
    } finally {
      setActionLoading(false);
    }
  };

  const revokeAllExceptCurrent = async () => {
    setActionLoading(true);
    setError('');
    try {
      await api.post('/api/admin/system/sessions/', {
        action: 'revoke_all_except_me'
      });
      await load();
    } catch (err) {
      setError(parseError(err, 'Failed to revoke sessions.'));
    } finally {
      setActionLoading(false);
    }
  };

  const kpis = useMemo(() => {
    const total = rows.length;
    const adminSessions = rows.filter((item) => item.role === 'admin').length;
    const uniqueUsers = new Set(rows.map((item) => item.user_id)).size;
    return { total, adminSessions, uniqueUsers };
  }, [rows]);

  return (
    <Stack spacing={2.5}>
      <Card sx={cardSx}>
        <CardContent sx={{ py: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" spacing={1.5}>
            <Stack direction="row" spacing={1.2} alignItems="center">
              <Avatar sx={{ bgcolor: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', width: 42, height: 42 }}>
                <ComputerOutlinedIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Active Sessions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Monitor active authenticated sessions and revoke suspicious access.
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1}>
              <Tooltip title="Refresh">
                <span>
                  <IconButton onClick={load} disabled={loading || actionLoading} sx={{ border: '1px solid', borderColor }}>
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<LogoutIcon />}
                onClick={revokeAllExceptCurrent}
                disabled={loading || actionLoading}
                sx={actionButtonSx}
              >
                Revoke Others
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Total Sessions
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {kpis.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Unique Users
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#0891b2' }}>
                {kpis.uniqueUsers}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Admin Sessions
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#f59e0b' }}>
                {kpis.adminSessions}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={cardSx}>
        <CardContent sx={{ p: 2 }}>
          <TextField
            fullWidth
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by email or username"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />
        </CardContent>
      </Card>

      <Card sx={cardSx}>
        <CardContent sx={{ p: 0 }}>
          {(loading || actionLoading) && <LinearProgress />}
          {error && (
            <Box sx={{ p: 2 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'rgba(245, 158, 11, 0.08)' }}>
                  <TableCell>User</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Device</TableCell>
                  <TableCell>IP</TableCell>
                  <TableCell>Last Seen</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Alert severity="info" sx={{ my: 1 }}>
                        No active sessions found.
                      </Alert>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.session_key} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {row.email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.username || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={row.role || 'user'} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {row.device_label || row.session_type || 'Session'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {row.session_key?.slice(0, 12)}...
                        </Typography>
                      </TableCell>
                      <TableCell>{row.ip_address || '-'}</TableCell>
                      <TableCell>{row.last_seen_at ? new Date(row.last_seen_at).toLocaleString() : '-'}</TableCell>
                      <TableCell>{row.expire_date ? new Date(row.expire_date).toLocaleString() : '-'}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          onClick={() => revokeSession(row.session_key)}
                          disabled={loading || actionLoading}
                        >
                          Revoke
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default AdminSessionsPage;
